/**
 * Presentation helpers.
 *
 * Dates in a service desk are read at a glance and compared constantly, so
 * relative phrasing beats an absolute timestamp for anything recent — "2h ago"
 * is instantly meaningful where "14:32" requires arithmetic. The exact value is
 * always available in a `title` attribute for when precision matters.
 */

/**
 * The one locale dates and numbers are written in.
 *
 * The interface is English, so its figures are too. Every formatter used to ask
 * for the browser's locale, and a browser set to Arabic wrote "٥ أكتوبر، ٠١:٠٨ م"
 * and "قبل ساعتين" beside English labels. Only the language is fixed: no time
 * zone is named, so every time is still shown in the person's own zone.
 */
export const INTERFACE_LOCALE = 'en-GB';

const RELATIVE = new Intl.RelativeTimeFormat(INTERFACE_LOCALE, { numeric: 'auto' });

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31_536_000_000],
  ['month', 2_592_000_000],
  ['week', 604_800_000],
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
];

export function relativeTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';

  const deltaMs = date.getTime() - Date.now();
  const absolute = Math.abs(deltaMs);

  if (absolute < 45_000) return 'just now';

  for (const [unit, ms] of UNITS) {
    if (absolute >= ms) return RELATIVE.format(Math.round(deltaMs / ms), unit);
  }
  return 'just now';
}

export function absoluteTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(INTERFACE_LOCALE, { dateStyle: 'full', timeStyle: 'short' }).format(date);
}

export function shortDate(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  // The year is noise for anything in the current year, and essential outside it.
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat(INTERFACE_LOCALE, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * A date somebody chose — a custom field's answer — as it reads.
 *
 * Stored as typed: "2026-10-05" for a date, "2026-10-05T14:30" for a date and
 * time, and shown raw the desk read ISO strings in the middle of a request. A
 * bare date is a day on the calendar rather than an instant, so it is
 * formatted as one: read as midnight, it slipped to the day before anywhere
 * west of Greenwich.
 */
export function calendarDate(value: string): string {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (day) {
    const date = new Date(Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3])));
    return new Intl.DateTimeFormat(INTERFACE_LOCALE, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  }
  const instant = toDate(value);
  return instant ? shortDate(instant) : value;
}

/**
 * How long until a deadline, phrased for urgency.
 *
 * The sign is returned separately so the caller can colour it. "20m" before a
 * breach and "20m" after one read almost identically as text and must never
 * look alike.
 */
export function untilDeadline(value: string | Date | null | undefined): {
  label: string;
  overdue: boolean;
  imminent: boolean;
} | null {
  const date = toDate(value);
  if (!date) return null;

  const deltaMs = date.getTime() - Date.now();
  const overdue = deltaMs < 0;
  const absolute = Math.abs(deltaMs);

  const minutes = Math.floor(absolute / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const magnitude =
    days >= 1 ? `${days}d` : hours >= 1 ? `${hours}h` : minutes >= 1 ? `${minutes}m` : 'now';

  return {
    label: overdue ? `${magnitude} overdue` : magnitude === 'now' ? 'due now' : `in ${magnitude}`,
    overdue,
    // Two hours is roughly the point at which an agent should reprioritise.
    imminent: !overdue && absolute < 2 * 3_600_000,
  };
}

/**
 * A length of time as "1h 5m".
 *
 * Rounded to the minute *before* it is split into hours and minutes. Rounding
 * only the remainder printed 59m 59s as "60m" and 1h 59m 50s as "1h 60m".
 */
export function duration(seconds: number): string {
  if (!seconds) return '0m';
  const total = Math.round(seconds / 60);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

/**
 * Plain text as the HTML body of a message.
 *
 * Replies and ticket descriptions are typed as plain text and emailed as HTML.
 * Wrapping the whole thing in one `<p>` kept the newlines as raw characters,
 * which HTML collapses — so "Hi Julia,\n\nKind regards,\nGrace" arrived as
 * "Hi Julia, Kind regards, Grace", and numbered steps ran into one line.
 *
 * Blank lines become paragraphs and single line breaks become `<br>`. Each
 * paragraph is `dir="auto"`, so a reply that opens in Arabic and signs off in
 * English lays out correctly both ways. Everything is escaped: this is the
 * person's text, not markup.
 */
export function textToHtml(text: string): string {
  const normalised = text.replace(/\r\n?/g, '\n').trim();
  if (!normalised) return '';
  return normalised
    .split(/\n{2,}/)
    .map((paragraph) => `<p dir="auto">${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Splits text into plain runs and web addresses, so a screen can make the
 * addresses clickable without ever treating the text as HTML.
 *
 * Trailing punctuation stays outside the link: "see https://x.example/a." links
 * to the address, not to the full stop.
 */
export function splitLinks(text: string): Array<{ text: string; href?: string }> {
  const parts: Array<{ text: string; href?: string }> = [];
  const pattern = /\bhttps?:\/\/[^\s<>"']+/gi;
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    let url = match[0];
    const trailing = /[.,;:!?)\]}'"»]+$/.exec(url);
    if (trailing) url = url.slice(0, url.length - trailing[0].length);
    const start = match.index ?? 0;
    if (start > last) parts.push({ text: text.slice(last, start) });
    parts.push({ text: url, href: url });
    last = start + url.length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts.at(-1)![0]}`.toUpperCase();
}

/**
 * A stable colour for an avatar, derived from the name.
 *
 * Deterministic, so the same person is the same colour on every screen — which
 * is what makes an avatar scannable in a dense list rather than decorative.
 */
export function avatarHue(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 360;
  }
  return hash;
}

/** An amount — a cost, a total — with up to two decimals, in the interface's figures. */
export function formatAmount(value: number): string {
  return new Intl.NumberFormat(INTERFACE_LOCALE, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value);
}

export function pluralise(count: number, singular: string, plural = `${singular}s`): string {
  return `${count.toLocaleString(INTERFACE_LOCALE)} ${count === 1 ? singular : plural}`;
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? null : date;
}
